/**
 * @architecture-header 1
 * @layer routes
 * @domain workspace-shell
 * @responsibility Render the search overlay with its filters, recents and empty state.
 * @state-owner overlay-local React state; TanStack Query owns the live search request state; the
 *   shared workspace-store's `selection`/`pendingSelection` (via go/setPendingSelection) on the
 *   Settings-leave path, consumed by WorkspaceChrome's mount effect
 * @allowed-imports React; workspace and dataset stores and hooks; role provider; modules/search
 *   public API (useTeacherWorkspaceSearch)
 * @forbidden direct API client; module internals
 * @public-api WorkspaceShell
 * @side-effects navigation; a debounced authenticated GET search request per typed query
 *   (via useTeacherWorkspaceSearch), rendering its loading/error/empty states
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts and
 *   SearchOverlay.test.tsx
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useActiveOrganizationId, useActiveRole } from '../../app/providers/ActiveRoleProvider';
import type { SearchEntityType } from '../../modules/search';
import { useTeacherWorkspaceSearch } from '../../modules/search';
import { MainEmpty } from '../../shared/components/ListColumn';
import { CloseIcon, SearchIcon } from '../../shared/components/VispoIcon';
import type { WorkspaceSection } from '../../shared/lib/model';
import { defaultSelection, findActivity, findRun } from '../../shared/lib/selectors';
import { useDatasetStore } from '../../shared/model/dataset-store';
import { useDebouncedValue, useTranslator } from '../../shared/model/use-workspace';
import type { NavigationSnapshot } from '../../shared/model/workspace-store';
import { useWorkspaceUiStore } from '../../shared/model/workspace-store';

const TEACHER_FILTER_TYPES: Readonly<Record<string, readonly SearchEntityType[]>> = {
  課程: ['classroom'],
  教材: ['curriculum'],
  活動: ['activity'],
  學生: ['student'],
};
const ALL_TEACHER_SEARCH_TYPES: readonly SearchEntityType[] = [
  'classroom',
  'curriculum',
  'activity',
  'student',
];

const CHIP_COLORS: Readonly<Record<string, readonly [string, string]>> = {
  課程: ['var(--primary-pale)', 'var(--primary)'],
  教材: ['rgba(99,102,241,.14)', '#4338ca'],
  活動: ['rgba(16,185,129,.14)', '#047857'],
  學生: ['var(--sc)', 'var(--on-surface-variant)'],
  對話: ['var(--error-container)', 'var(--error)'],
  資源: ['var(--warn-pale)', 'var(--warn)'],
};

export function SearchOverlay({
  onClose,
  onLeaveToSection,
  open,
}: {
  onClose(): void;
  /** Leaves the current route for the picked section instead of a store-only jump (e.g. Settings). */
  onLeaveToSection?(section: WorkspaceSection): void;
  open: boolean;
}) {
  const t = useTranslator();
  const role = useActiveRole();
  const teacher = role === 'teacher';
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('全部');
  const inputRef = useRef<HTMLInputElement>(null);
  const go = useWorkspaceUiStore((state) => state.go);
  const setPendingSelection = useWorkspaceUiStore((state) => state.setPendingSelection);
  const navigateSnapshot = useWorkspaceUiStore((state) => state.navigateSnapshot);
  const recent = useWorkspaceUiStore((state) => state.recent);
  const classes = useDatasetStore((state) => state.classes);
  const activities = useDatasetStore((state) => state.activities);
  const runs = useDatasetStore((state) => state.runs);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    const timeout = window.setTimeout(() => inputRef.current?.focus(), 30);
    return () => window.clearTimeout(timeout);
  }, [open]);

  const filters = teacher ? ['全部', '課程', '教材', '活動', '學生'] : ['全部', '活動'];
  // The 5th slot is a navigation-id override: '' means "no specific id, fall back to
  // defaultSelection" (unchanged local-preview behavior below), while a live search row (built
  // separately) carries its real backend id so a click lands on that exact result.
  const source: readonly (readonly [string, string, string, string, string])[] = teacher
    ? [
        ...activities.map(
          (activity) =>
            ['活動', activity.name, `${activity.questionCount} ${t('題')}`, 'act', ''] as const,
        ),
        ...classes.flatMap((classroom) => [
          ['課程', classroom.name, classroom.subtitle, 'room', ''] as const,
          ...classroom.students.map(
            (student) => ['學生', student, classroom.name, 'rep', ''] as const,
          ),
        ]),
      ]
    : runs.flatMap((run) => {
        const activity = findActivity(activities, run.activityId);
        const classroom = classes[run.classIndex];
        return activity && classroom
          ? [['活動', activity.name, classroom.name, 'act', ''] as const]
          : [];
      });
  const trimmed = query.trim();
  // The search endpoint requires a non-blank q, so the "browse without typing" preview above
  // keeps reading from the dataset store (unchanged); only a typed query switches to the live
  // cross-domain search, debounced so it does not fire on every keystroke.
  const debouncedQuery = useDebouncedValue(trimmed, 250);
  const organizationId = useActiveOrganizationId();
  const searchTypes =
    filter === '全部'
      ? ALL_TEACHER_SEARCH_TYPES
      : (TEACHER_FILTER_TYPES[filter] ?? ALL_TEACHER_SEARCH_TYPES);
  const searchQuery = useTeacherWorkspaceSearch(
    teacher ? organizationId : null,
    teacher ? debouncedQuery : '',
    teacher ? searchTypes : [],
  );
  const liveRows = useMemo<readonly (readonly [string, string, string, string, string])[]>(() => {
    if (!searchQuery.data) return [];
    return [
      ...searchQuery.data.classroom.map(
        (row) => ['課程', row.title, row.detail, row.section, row.id] as const,
      ),
      ...searchQuery.data.curriculum.map(
        (row) => ['教材', row.title, row.detail, row.section, row.id] as const,
      ),
      ...searchQuery.data.activity.map(
        (row) => ['活動', row.title, row.detail, row.section, row.id] as const,
      ),
      ...searchQuery.data.student.map(
        (row) => ['學生', row.title, row.detail, row.section, row.id] as const,
      ),
    ];
  }, [searchQuery.data]);
  const usingLiveSearch = teacher && Boolean(debouncedQuery);
  const liveSearchLoading = usingLiveSearch && searchQuery.isLoading;
  const liveSearchErrored = usingLiveSearch && searchQuery.isError;
  const rows = usingLiveSearch
    ? liveRows.filter((row) => filter === '全部' || row[0] === filter)
    : source
        .filter((row) => filter === '全部' || row[0] === filter)
        .filter((row) => !trimmed || row[1].includes(trimmed) || row[2].includes(trimmed));

  const openRow = (section: string, id: string) => {
    onClose();
    const target = section as WorkspaceSection;
    // A live search result carries its own real backend id, so a click lands on that exact
    // result; the local browse-without-typing preview has no such id ('') and keeps falling back
    // to defaultSelection.
    const resolvedSelection = id || defaultSelection(target, role, classes, runs);
    // The store selection is set immediately even when leaving to a different route
    // (Settings-shell path, onLeaveToSection): the destination reads `selection` from this same
    // persistent store, not from the URL, so skipping `go` there silently dropped the picked
    // result (SH-F02 round-2 rework, Sol BLOCKING finding 6).
    go(target, resolvedSelection);
    if (onLeaveToSection) {
      // That write alone does not survive the REAL route transition: the destination's
      // WorkspaceChrome unconditionally re-applies its own mount-time default the first time
      // that section mounts (Sol BLOCKING round-3 finding 3). setPendingSelection queues the SAME
      // value for that mount effect to consume once, so it yields to the picked result instead of
      // overwriting it. Queued ONLY on this leave path -- the ordinary in-place path has no mount
      // to consume it, and a store subscriber (workspace-store.ts) clears any queued entry the
      // moment section/selection change to anything other than its own target, so a stale entry
      // can never survive to resurrect on a later, unrelated mount (SH-F02 rounds 4 and 5, Sol
      // BLOCKING finding 1 both times).
      if (resolvedSelection) setPendingSelection(target, resolvedSelection);
      onLeaveToSection(target);
    }
  };

  const openRecent = (snapshot: NavigationSnapshot) => {
    onClose();
    if (onLeaveToSection) onLeaveToSection(snapshot.section);
    else navigateSnapshot(snapshot);
  };

  const recentLabel = (snapshot: NavigationSnapshot) => {
    const section =
      snapshot.section === 'act'
        ? t('活動')
        : snapshot.section === 'room'
          ? t('班級')
          : snapshot.section === 'live'
            ? t('即時')
            : t('報告');
    const open = snapshot.open;
    const openTarget =
      (open?.id &&
        (findActivity(activities, open.id)?.name ?? findRun(runs, open.id)?.activityId)) ||
      undefined;
    const run = findRun(runs, snapshot.selection);
    const selectedActivity = findActivity(activities, snapshot.selection);
    const target =
      (open?.n && (open.k === 'stu' || open.k === 'iv') ? open.n : undefined) ??
      (openTarget ? (findActivity(activities, openTarget)?.name ?? openTarget) : undefined) ??
      selectedActivity?.name ??
      (run ? findActivity(activities, run.activityId)?.name : undefined) ??
      (snapshot.section === 'room' && snapshot.selection?.startsWith('c')
        ? classes[Number(snapshot.selection.slice(1))]?.name
        : snapshot.selection);
    return target ? `${section} · ${target}` : section;
  };

  const renderRow = (row: readonly [string, string, string, string, string]) => {
    const colors = CHIP_COLORS[row[0]] ?? CHIP_COLORS.活動;
    return (
      <button className="sr" key={row[1]} type="button" onClick={() => openRow(row[3], row[4])}>
        <span
          className="sq2 sq"
          style={{
            width: 32,
            height: 32,
            borderRadius: 'var(--r)',
            display: 'grid',
            placeItems: 'center',
            background: colors[0],
            color: colors[1],
            fontSize: 13,
            fontWeight: 700,
            flex: '0 0 32px',
          }}
        >
          {row[0][0]}
        </span>
        <span className="t">
          <b>{row[1]}</b>
          <span>{row[2]}</span>
        </span>
        <em>{row[0]}</em>
      </button>
    );
  };

  const renderRecentRow = (snapshot: NavigationSnapshot) => (
    <button
      aria-label={recentLabel(snapshot)}
      className="sr"
      key={`${snapshot.section}:${snapshot.selection ?? ''}:${JSON.stringify(snapshot.open ?? null)}`}
      type="button"
      onClick={() => openRecent(snapshot)}
    >
      <span
        className="sq2 sq"
        style={{
          width: 32,
          height: 32,
          borderRadius: 'var(--r)',
          display: 'grid',
          placeItems: 'center',
          background: 'var(--primary-pale)',
          color: 'var(--primary)',
          fontSize: 13,
          fontWeight: 700,
          flex: '0 0 32px',
        }}
      >
        {recentLabel(snapshot).slice(0, 1)}
      </span>
      <span className="t">
        <b>{recentLabel(snapshot)}</b>
        <span>{t('最近開過的')}</span>
      </span>
      <em>{t('最近')}</em>
    </button>
  );

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: the scrim is a shortcut; Escape and the panel controls are the accessible paths
    <div
      className={`sov${open ? ' on' : ''}`}
      id="sov"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div className="spanel">
        <div className="sbar">
          <SearchIcon />
          <input
            aria-label={t('輸入指令或搜尋')}
            autoComplete="off"
            id="sq"
            name="workspace-search"
            onChange={(event) => setQuery(event.target.value)}
            placeholder={`${t('輸入指令或搜尋')}…`}
            ref={inputRef}
            spellCheck={false}
            value={query}
          />
          <button aria-label={t('關閉')} className="sclose" type="button" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        <div className="sfil" id="sfil">
          {filters.map((item) => (
            <button
              className={filter === item ? 'on' : ''}
              key={item}
              type="button"
              onClick={() => setFilter(item)}
            >
              {t(item)}
            </button>
          ))}
        </div>
        <div className="sres" id="sres">
          {!trimmed && filter === '全部' && (recent.length || source.length) ? (
            <>
              <div className="sgrp">{t('最近開過的')}</div>
              {recent.length ? recent.map(renderRecentRow) : null}
              {source.slice(0, 4).map(renderRow)}
              <div className="sempty" style={{ padding: '34px 20px 40px' }}>
                <div className="glyphs">
                  {Array.from({ length: 15 }, (_, index) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: decorative glyph grid
                    <i key={index} />
                  ))}
                </div>
                <b>{t('搜尋 Vispo')}</b>
                <p>{teacher ? t('活動與學生都在這裡找得到') : t('你的作業都在這裡找得到')}</p>
              </div>
            </>
          ) : liveSearchLoading ? (
            <div className="sempty" style={{ padding: '34px 20px 40px' }}>
              <b>{t('搜尋中…')}</b>
            </div>
          ) : liveSearchErrored ? (
            <div className="sempty" style={{ padding: '34px 20px 40px' }}>
              <b>{t('搜尋時發生問題')}</b>
              <p>{t('請再試一次。')}</p>
              <button className="bt pri" type="button" onClick={() => void searchQuery.refetch()}>
                {t('重試')}
              </button>
            </div>
          ) : rows.length ? (
            rows.map(renderRow)
          ) : (
            <MainEmpty
              description={
                trimmed
                  ? t('換個關鍵字，或改用上面的分類看看。')
                  : t('建立班級或活動後，就可以從這裡快速找到。')
              }
              title={trimmed ? `找不到「${trimmed}」` : t('目前沒有可搜尋的內容')}
            />
          )}
        </div>
      </div>
    </div>
  );
}
